import logoSvg from "@/assets/favicon.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { getEnabledExternalLinks } from "@/lib/external-links";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { navigateTo } from "@/lib/url-utils";
import { Link, useLocation } from "@tanstack/react-router";
import {
  Code2,
  CreditCard,
  ExternalLink,
  FileText,
  Home,
  Key,
  LogOut,
  Menu,
  Settings,
  User,
  X
} from "lucide-react";
import { ReactNode, useState, useEffect } from "react";
import { Logo } from "@/components/ui/logo";
import { SocialIcon } from "react-social-icons";

interface DashboardLayoutProps {
  children: ReactNode;
}

const navigation = [
  { name: "Overview", href: "/dashboard", icon: Home },
  // { name: "Playground", href: "/dashboard/playground", icon: Code2 },
  { name: "API Keys", href: "/dashboard/api-keys", icon: Key },
  { name: "Billing", href: "/dashboard/billing", icon: CreditCard },
  { name: "Settings", href: "/dashboard/settings", icon: Settings },
];

const externalLinks = [
  { name: "Documentation", href: "https://plusefin.com/docs/", icon: FileText },
  { name: "MCP Tools", href: "https://plusefin.com/docs/tools/", icon: ExternalLink },
];

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigateTo("/");
  };

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile only
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [sidebarOpen]);

  // Get enabled external links from configuration
  const enabledExternalLinks = getEnabledExternalLinks();

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gray-50">
        {/* Mobile sidebar backdrop - only on mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile sidebar */}
        <div className={cn(
          "fixed inset-y-0 left-0 flex w-72 flex-col bg-white z-50 transform transition-transform duration-300 ease-in-out lg:hidden",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <div className="flex flex-col flex-grow">
            {/* Mobile header with close button */}
            <div className="flex h-16 items-center justify-between px-4">
              <Link to="/dashboard" className="flex items-center space-x-2">
                <img src={logoSvg} alt="PlusE Finance" className="w-8 h-8" />
                <span className="text-xl font-bold">PlusE Finance</span>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <SidebarContent
              currentPath={location.pathname}
              onSignOut={handleSignOut}
            />
          </div>
        </div>

        {/* Desktop sidebar - always visible, no animations */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col z-50 mt-[60px]">
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
            <SidebarContent
              currentPath={location.pathname}
              onSignOut={handleSignOut}
            />
          </div>
        </div>

        {/* Main content */}
        <div className="lg:pl-72">
          {/* Top bar */}
          <div className="lg:hidden sticky top-0 z-30 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            {/* Mobile menu button - only visible on mobile */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </Button>
          </div>

          {/* Page content */}
          <main className="py-10 pt-5">
            <div className="px-4 sm:px-6 lg:px-8">{children}</div>
            <div className="mt-10">
              <site-footer sitePath="/" currentYear={new Date().getFullYear()}></site-footer>
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider >
  );
}

function SidebarContent({
  currentPath,
  onSignOut,
}: {
  currentPath: string;
  onSignOut: () => void;
}) {
  const { user } = useAuth();

  return (
    <div className="flex flex-col flex-grow">
      {/* User info */}
      <div className="px-4 py-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center space-x-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.user_metadata?.avatar_url} />
              <AvatarFallback className="bg-blue-100 text-blue-600">
                {user?.email?.charAt(0).toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Personal</p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || "user@example.com"}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-1">
        {navigation.map((item) => {
          const isActive = currentPath === item.href;
          return (
            <Tooltip key={item.name}>
              <TooltipTrigger asChild>
                <Link
                  to={item.href}
                  className={cn(
                    "group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors",
                    isActive
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <item.icon
                    className={cn(
                      "mr-3 h-5 w-5 transition-colors",
                      isActive
                        ? "text-blue-500"
                        : "text-gray-400 group-hover:text-gray-500"
                    )}
                  />
                  {item.name}
                </Link>
              </TooltipTrigger>
            </Tooltip>
          );
        })}
      </nav>

      <Separator />

      {/* External links */}
      <div className="px-4 py-4">
        <div className="space-y-1">
          {externalLinks.map((item) => (
            <Tooltip key={item.name}>
              <TooltipTrigger asChild>
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <item.icon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                  {item.name}
                  <ExternalLink className="ml-auto h-4 w-4 text-gray-400" />
                </a>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Open {item.name}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
}
